// pages/Offerwall/index.tsx
import React, { useEffect, useState } from 'react';
import * as OW from './index.styled';
import DataTable from '@/components/@common/DataTable';
import { CampaignData, CampaignResponse } from '@/@types/CampaignData';
import axiosRequest from '@/api';
import { ResData } from '@/@types';
import {
    Tooltip,
    message,
    Space,
    Button,
    Input,
    Select,
    Form,
    Popconfirm,
    DatePicker,
    Statistic,
    Tag
} from 'antd';
import { TablePaginationConfig } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    SearchOutlined,
    CalendarOutlined,
    BarChartOutlined,
    MobileOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { userAtom } from '@/recoil/atoms/user.atom';
import { AgencyData } from '@/@types/Agency';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import SimplePinManager from '../Campaign/SimplePinManager';

dayjs.locale('ko');

const { Option } = Select;

// 오퍼월 통계 타입
interface OfferwallStats {
    total_campaigns: number;
    total_rewards_today: number;
    max_available_rewards: number;
}

function Offerwall() {
    const [user, setUser] = useRecoilState(userAtom);
    const [searchText, setSearchText] = useState<string>('');
    const [searchedColumn, setSearchedColumn] = useState<string>('');
    const navigate = useNavigate();
    const [agencies, setAgencies] = useState<AgencyData[]>([]);

    // 상태 관리
    const [offerwallData, setOfferwallData] = useState<CampaignData[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // 날짜 선택
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [stats, setStats] = useState<OfferwallStats>({
        total_campaigns: 0,
        total_rewards_today: 0,
        max_available_rewards: 0
    });

    const [filterForm] = Form.useForm();

    // 대행사 목록 조회
    const fetchAgencies = async () => {
        try {
            const response = await axiosRequest.requestAxios<ResData<AgencyData[]>>('get', '/agencies');
            setAgencies(response.data);
        } catch (error) {
            console.error("fetchAgencies error: ", error);
        }
    };

    // 오퍼월 데이터 조회
    const fetchOfferwallData = async (
        searchValue: string,
        currentPage: number,
        pageSize: number,
        targetDate?: string
    ) => {
        setLoading(true);
        try {
            const hasQueryParams = searchValue.includes('=');
            const dateParam = targetDate ? `target_date=${targetDate}` : `target_date=${selectedDate.format('YYYY-MM-DD')}`;

            const endpoint = searchValue
                ? hasQueryParams
                    ? `/offerwall?${searchValue}&${dateParam}&page=${currentPage}&size=${pageSize}`
                    : `/offerwall?query=${searchValue}&${dateParam}&page=${currentPage}&size=${pageSize}`
                : `/offerwall?${dateParam}&page=${currentPage}&size=${pageSize}`;

            const response = await axiosRequest.requestAxios<ResData<CampaignResponse>>('get', endpoint);
            const { total_count, result } = response.data;

            const transformedData = result.map((item, index) => {
                return {
                    ...item,
                    number: (currentPage - 1) * pageSize + index + 1,
                    period: `${item.start_date} ~ ${item.end_date}`,
                };
            });

            setOfferwallData(transformedData);
            setTotalCount(total_count);
        } catch (error) {
            console.error('Error fetching offerwall data:', error);
            message.error('오퍼월 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 오퍼월 통계 조회
    const fetchOfferwallStats = async (targetDate?: string) => {
        try {
            const dateParam = targetDate || selectedDate.format('YYYY-MM-DD');
            const response = await axiosRequest.requestAxios<ResData<OfferwallStats>>(
                'get',
                `/offerwall/stats?target_date=${dateParam}`
            );
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching offerwall stats:', error);
        }
    };

    // 노출/미노출 토글
    const toggleVisibility = async (campaignId: number, currentVisibility: boolean) => {
        try {
            await axiosRequest.requestAxios('post', `/offerwall/${campaignId}/toggle-visibility`);

            const statusText = !currentVisibility ? '노출' : '미노출';
            message.success(`캠페인이 ${statusText} 상태로 변경되었습니다.`);

            // 데이터 새로고침
            fetchOfferwallData(searchText, currentPage, pageSize);
            fetchOfferwallStats();
        } catch (error) {
            console.error('Error toggling visibility:', error);
            message.error('노출 상태 변경에 실패했습니다.');
        }
    };

    // 문구(title) 노출/숨김 토글
    const toggleTitleVisibility = async (campaignId: number, currentHideTitle: boolean) => {
        try {
            await axiosRequest.requestAxios('post', `/offerwall/${campaignId}/toggle-title-visibility`);

            const statusText = !currentHideTitle ? '숨김' : '노출';
            message.success(`캠페인 문구가 ${statusText} 상태로 변경되었습니다.`);

            // 데이터 새로고침
            fetchOfferwallData(searchText, currentPage, pageSize);
        } catch (error) {
            console.error('Error toggling title visibility:', error);
            message.error('문구 노출 상태 변경에 실패했습니다.');
        }
    };

    // 오퍼월 뷰 팝업
    const showOfferwallView = (targetDate: string) => {
        const width = 375;
        const height = 667;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        window.open(
            `/admin/offerwall/view/${targetDate}`,
            'offerwallViewPopup',
            `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=yes`
        );
    };

    // 캠페인 수정 페이지로 이동
    const handleEdit = (record: CampaignData) => {
        navigate(`/admin/campaign/add/${record.id}`);
    };

    // 검색 관련
    const handleSearch = (selectedKeys: React.Key[], confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0] as string);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: string): ColumnsType<CampaignData>[0] => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            if (recordValue !== undefined && recordValue !== null) {
                return recordValue
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase());
            }
            return false;
        },
    });

    // 텍스트 줄임 함수
    function truncateText(text: string, limit: number) {
        if (text && text.length > limit) {
            return text.substring(0, limit) + '...';
        }
        return text;
    }

    // 테이블 컬럼 정의
    const columns: ColumnsType<CampaignData> = [
        {
            title: 'No',
            dataIndex: 'number',
            key: 'number',
            width: 60,
            fixed: 'left',
        },
        {
            title: '대행사',
            dataIndex: 'agency_name',
            key: 'agency_name',
            width: 100,
            render: (text: string) => truncateText(text, 10),
        },
        {
            title: '광고주',
            dataIndex: 'advertiser_name',
            key: 'advertiser_name',
            width: 100,
            render: (text: string) => truncateText(text, 10),
        },
        {
            title: '캠페인 제목',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: CampaignData) => {
                const title = text || record.title || '제목 없음';
                return (
                    record.id ? (
                        <a onClick={() => handleEdit(record)}>
                            <Tooltip title={title}>
                                {truncateText(title, 15)}
                            </Tooltip>
                        </a>
                    ) : (
                        <span>ID 없음</span>
                    )
                );
            },
            ...getColumnSearchProps('title'),
        },
        {
            title: '광고 단가',
            dataIndex: 'points_per_call',
            key: 'points_per_call',
            width: 100,
            render: (value: number) => `${value?.toLocaleString()}원`,
        },
        {
            title: '고객 리워드',
            dataIndex: 'points_per_participant',
            key: 'points_per_participant',
            width: 100,
            render: (value: number) => `${value?.toLocaleString()}원`,
        },
        {
            title: '기간',
            dataIndex: 'period',
            key: 'period',
            width: 150,
            render: (text: string) => {
                const [startDate, endDate] = text.split(' ~ ');
                return (
                    <span style={{ fontSize: '12px' }}>
                        {startDate} ~<br />
                        {endDate === 'null' ? '' : endDate}
                    </span>
                );
            },
        },
        {
            title: '상품',
            dataIndex: 'product_name',
            key: 'product_name',
            width: 120,
            render: (text: string) => truncateText(text, 10),
        },
        {
            title: '적립성공',
            dataIndex: 'participation_count_r',
            key: 'daily_success',
            width: 80,
            render: (count: number, record: CampaignData) => {
                return (
                    <OW.CountContainer>
                        <OW.CounterSection>
                            <OW.DailyCount>
                                <a onClick={() => navigate(`/admin/log/${record.id}`)}>
                                    {count || 0}
                                </a>
                            </OW.DailyCount>
                        </OW.CounterSection>
                    </OW.CountContainer>
                );
            },
        },
        {
            title: '누적',
            dataIndex: 'total_rewards',
            key: 'total_rewards',
            width: 80,
            render: (count: number, record: CampaignData) => {
                return (
                    <OW.CountContainer>
                        <OW.CounterSection>
                            <OW.TotalCount>
                                <a onClick={() => navigate(`/admin/log/${record.id}`)}>
                                    {count || 0}
                                </a>
                            </OW.TotalCount>
                        </OW.CounterSection>
                    </OW.CountContainer>
                );
            },
        },
        {
            title: '페이지 조회수',
            dataIndex: 'screen_view_count',
            key: 'screen_view_count',
            width: 100,
            render: (count: number) => count?.toLocaleString() || 0,
        },
        {
            title: '문의 사항',
            dataIndex: 'inquiry_count', // ✅ dataIndex를 inquiry_count로 변경
            key: 'inquiry_count',
            width: 100,
            render: (count: number, record: any) => (
                <Tooltip title={count > 0 ? "문의사항 보기" : "문의사항 없음"}>
                    <a
                        onClick={() => navigate(`/admin/campaign/inquiry/${record.id}`)}
                        style={{
                            color: count > 0 ? '#1890ff' : '#999',
                            fontWeight: count > 0 ? 'bold' : 'normal',
                            cursor: 'pointer'
                        }}
                    >
                        {count || 0}
                    </a>
                </Tooltip>
            ),
        },
        {
            title: '오퍼월 노출',
            dataIndex: 'is_visible',
            key: 'is_visible',
            width: 100,
            render: (is_visible: boolean, record: CampaignData) => {
                const currentStatus = is_visible ? '노출' : '미노출';

                if (user?.role_name === 'admin' || user?.role_name === 'super') {
                    return (
                        <Popconfirm
                            title="노출 상태 변경"
                            description={`이 캠페인을 ${!is_visible ? '노출' : '미노출'} 상태로 변경하시겠습니까?`}
                            onConfirm={() => toggleVisibility(Number(record.id), is_visible)}
                            okText="확인"
                            cancelText="취소"
                        >
                            <OW.VisibilityTag visible={is_visible}>
                                {currentStatus}
                            </OW.VisibilityTag>
                        </Popconfirm>
                    );
                }

                return <OW.VisibilityTag visible={is_visible}>{currentStatus}</OW.VisibilityTag>;
            },
        },
        {
            title: '문구 노출',
            dataIndex: 'hide_title',
            key: 'hide_title',
            width: 100,
            render: (hide_title: boolean, record: CampaignData) => {
                const currentStatus = hide_title ? '숨김' : '노출';

                if (user?.role_name === 'admin' || user?.role_name === 'super') {
                    return (
                        <Popconfirm
                            title="문구 노출 상태 변경"
                            description={`이 캠페인의 문구를 ${hide_title ? '노출' : '숨김'} 상태로 변경하시겠습니까?`}
                            onConfirm={() => toggleTitleVisibility(Number(record.id), hide_title)}
                            okText="확인"
                            cancelText="취소"
                        >
                            <OW.VisibilityTag visible={!hide_title}>
                                {currentStatus}
                            </OW.VisibilityTag>
                        </Popconfirm>
                    );
                }

                return <OW.VisibilityTag visible={!hide_title}>{currentStatus}</OW.VisibilityTag>;
            },
        },
        {
            title: '고정 순서',
            key: 'pin_priority',
            width: 140,
            render: (text: any, record: CampaignData) => {

                if (!record.id) {
                    return <span>ID 없음</span>;
                }
                // 관리자만 변경 가능
                if (user?.role_name === 'admin' || user?.role_name === 'super') {
                    return (
                        <SimplePinManager
                            campaignId={record.id}
                            currentPriority={record.pin_priority || 1}
                            onPriorityChange={() => {
                                // 테이블 새로고침
                                fetchOfferwallData(searchText, currentPage, pageSize);
                            }}
                        />
                    );
                }

                // 일반 사용자는 보기만
                const getPriorityLabel = (priority: number) => {
                    if (priority >= 100) return '최상단 고정';
                    if (priority >= 90) return '차상단 고정';
                    if (priority > 1) return '일반 고정';
                    return '고정 안함';
                };

                const getPriorityColor = (priority: number) => {
                    if (priority >= 100) return 'red';
                    if (priority >= 90) return 'orange';
                    if (priority > 1) return 'blue';
                    return 'default';
                };

                return (
                    <Tag color={getPriorityColor(record.pin_priority || 1)}>
                        {getPriorityLabel(record.pin_priority || 1)}
                    </Tag>
                );
            }
        },
    ];

    // 날짜 변경 핸들러
    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            setSelectedDate(date);
            const targetDate = date.format('YYYY-MM-DD');
            fetchOfferwallData(searchText, currentPage, pageSize, targetDate);
            fetchOfferwallStats(targetDate);
        }
    };

    // 페이지네이션 변경
    const handlePaginationChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    // 필터 변경
    const handleFilterChange = (field: string, value: string | null) => {
        if (field === 'agency_id') {
            if (value) {
                const agencyId = value;
                fetchOfferwallData(`agency_id=${agencyId}${searchText ? `&query=${searchText}` : ''}`, currentPage, pageSize);
            } else {
                fetchOfferwallData(searchText, currentPage, pageSize);
            }
        }
    };

    const handleOfferwallViewClick = () => {
        const width = 375; // 모바일 화면 너비 (조정 가능)
        const height = 667; // 모바일 화면 높이 (조정 가능)
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const targetDate = selectedDate.format('YYYY-MM-DD');
        console.log(`heyheyhey${targetDate}`)

        window.open(
            `/admin/mobilelistview?target_date=${targetDate}`,
            'mobileViewPopup',
            `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=yes`
        );
        return false; // 기본 이벤트 방지
    };

    // 초기 데이터 로딩
    useEffect(() => {
        fetchAgencies();
    }, []);

    useEffect(() => {
        fetchOfferwallData(searchText, currentPage, pageSize);
        fetchOfferwallStats();
    }, [searchText, currentPage, pageSize]);

    return (
        <OW.Wrapper>
            {/* 상단 통계 및 날짜 선택 */}
            <OW.HeaderSection>
                <OW.StatsContainer>
                    <OW.StatCard>
                        <Statistic
                            title="오퍼월 노출 캠페인"
                            value={stats.total_campaigns}
                            suffix="개"
                            prefix={<BarChartOutlined />}
                        />
                    </OW.StatCard>
                    <OW.StatCard>
                        <Statistic
                            title={`리워드 합계: ${selectedDate.format('YYYY-MM-DD')}`}
                            value={stats.max_available_rewards}
                            suffix="원"
                            prefix={<CalendarOutlined />}
                        />
                    </OW.StatCard>
                </OW.StatsContainer>

                <OW.DateSection>
                    <Space>
                        <DatePicker
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="YYYY-MM-DD"
                            placeholder="조회 날짜 선택"
                        />
                        <Button
                            type="primary"
                            icon={<MobileOutlined />}
                            onClick={handleOfferwallViewClick}
                        >
                            오퍼월 뷰
                        </Button>
                    </Space>
                </OW.DateSection>
            </OW.HeaderSection>

            {/* 데이터 테이블 */}
            <DataTable
                data={offerwallData}
                totalCount={totalCount}
                columns={columns}
                onChange={handlePaginationChange}
                tableTitle="오퍼월 관리"
                loading={loading}
                onFilterChange={handleFilterChange}
                showAgencyFilter={user?.role_name === "super"}
            // hideAddButton={true} // 오퍼월에서는 추가 버튼 숨김
            // hideMobileListButton={true} // 모바일 리스트 버튼도 숨김
            />
        </OW.Wrapper>
    );
}

export default Offerwall;